"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import * as React from "react";
import { Download } from "lucide-react";
import { usePlayerStore, useSiteAuthStore } from "@gerama/stores";
import { GeramaLogo } from "@gerama/ui";
import { cn } from "@gerama/utils";
import { BRAND } from "@/lib/brand";
import {
  DISCOVER_ITEMS,
  isActivePath,
  LIBRARY_ITEMS,
  LIBRARY_TITLE,
  MORE_ITEMS,
  NAV_ITEMS,
} from "./nav-items";

const ITEM = "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors";

/**
 * One sidebar panel: brand, browse, catalogue, the personal library (signed-in
 * only), then blog/plans and the app download. Section titles are text only;
 * a single rule separates each group.
 */
export function AppSidebar() {
  const hasTrack = usePlayerStore((s) => s.queue.length > 0);
  return (
    <aside
      className={cn(
        "bg-sidebar text-sidebar-foreground sticky top-2 hidden w-64 shrink-0 flex-col rounded-xl md:flex lg:w-72",
        hasTrack ? "h-[calc(100dvh-6.5rem)]" : "h-[calc(100dvh-1rem)]",
      )}
      aria-label="ناوبری"
    >
      <Link href="/" aria-label={BRAND} className="flex items-center px-5 pt-5 pb-3">
        <GeramaLogo className="h-9" title="" />
      </Link>

      <nav className="min-h-0 flex-1 overflow-y-auto px-3 pb-2">
        <ItemList items={NAV_ITEMS} />
        <Divider />
        <ItemList items={DISCOVER_ITEMS} />
        <LibraryGroup />
        <Divider />
        <SectionTitle>بیشتر</SectionTitle>
        {/* Download has its own footer link at the bottom of the sidebar. */}
        <ItemList items={MORE_ITEMS.filter((item) => item.href !== "/download")} />
      </nav>

      <DownloadFooter />
    </aside>
  );
}

function ItemList({
  items,
}: {
  items: readonly {
    href: string;
    title: string;
    icon: React.ComponentType<{ className?: string }>;
  }[];
}) {
  const pathname = usePathname();
  return (
    <ul className="space-y-0.5">
      {items.map((item) => {
        const active = isActivePath(pathname, item.href);
        const Icon = item.icon;
        return (
          <li key={item.href}>
            <Link
              href={item.href}
              aria-current={active ? "page" : undefined}
              className={cn(
                ITEM,
                active
                  ? "bg-sidebar-accent text-sidebar-foreground"
                  : "text-muted-foreground hover:text-sidebar-foreground",
              )}
            >
              <Icon className="size-5 shrink-0" />
              {item.title}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function Divider() {
  return <div className="border-border/40 my-3 border-t" />;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <p className="text-muted-foreground px-3 pt-1 pb-1 text-sm font-medium">{children}</p>;
}

function LibraryGroup() {
  const authed = useSiteAuthStore((s) => !!s.token);
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);
  if (!mounted || !authed) return null;
  return (
    <>
      <Divider />
      <SectionTitle>{LIBRARY_TITLE}</SectionTitle>
      <ItemList items={LIBRARY_ITEMS} />
    </>
  );
}

/** Every platform lives on /download — stores, direct APK, desktop, TV and the web app. */
function DownloadFooter() {
  const pathname = usePathname();
  return (
    <div className="border-border/40 mx-3 mb-2 border-t pt-2">
      <Link
        href="/download"
        className={cn(
          ITEM,
          isActivePath(pathname, "/download")
            ? "text-sidebar-foreground"
            : "text-muted-foreground hover:text-sidebar-foreground",
        )}
      >
        <Download className="size-5 shrink-0" />
        دانلود اپلیکیشن
      </Link>
    </div>
  );
}
